import type {
  ProductCosts,
  ProductDTO,
  ProductMetricDTO,
  ProductTransactionData,
  SwapTypeAcceptance,
} from '@psw/shared';
import { Attribute } from './Attribute';

export class Product implements ProductDTO {
  _id?: string;
  _key?: string;
  attributes?: Attribute[];
  embed_vector?: number[];
  item_description_english?: string;
  item_description_local_language?: string;
  months?: number[];
  packs_vals?: number[];
  possible_volumes?: { unit: string; value: number }[];
  supplied_item_id?: string | number;
  supplier_item_code?: string;
  spend_vals?: number[];
  swap_info?: {
    option_number: number;
    pack_volume_unit: string;
    pro_rated_pack_volume: number;
    savings: number | null;
    similarity: number;
    swap_rule: SwapTypeAcceptance;
    swap_metrics: ProductMetricDTO[];
  };
  tagged_description?: string;
  taxons?: { _id: string }[];
  url?: string;
  url_image?: string;
  vector?: number[];
  volume_vals?: number[];
  product_transaction_data?: ProductMetricDTO[];
  product_costs?: ProductMetricDTO[];

  constructor(product: ProductDTO) {
    Object.assign(this, product);
    this.attributes = product.attributes?.map(a => new Attribute(a));
  }

  getHighestTaxonomy(taxonomy: string) {
    return this.taxons
      ?.filter(t => t._id.includes(taxonomy))
      .sort((a, b) => a._id.length - b._id.length)
      .pop();
  }

  getAttribute(key: string) {
    if (key.split('/').length === 1) return this.attributes?.find(a => a._id === `Attributes/${key}`);
    if (key.split('/').length === 2 && key.split('/')[0] === 'Attributes')
      return this.attributes?.find(a => a._id === key);
    throw new Error(
      `Invalid attribute key: ${key}. It should be in the format 'Attributes/attributeId' or 'attributeId'`,
    );
  }

  getMetric(id: string): ProductMetricDTO {
    const empty = {} as ProductMetricDTO;
    const [rootKey, _] = id.split('/');
    if (!['product_costs', 'product_transaction_data'].includes(rootKey)) {
      return empty;
    }
    const metric = this?.[rootKey]?.find(elem => elem?._id === id);
    return metric || empty;
  }

  getMetricValue(id: ProductCosts | ProductTransactionData): number | null {
    return this.getMetric(id)?.value;
  }
}
